import { useState, useEffect, useCallback, useRef } from 'react';

export interface GameEvent {
  type: string;
  data: any;
  timestamp: number;
  txHash?: string;
  block?: number;
}

export interface GameState {
  round: number;
  pot: string;
  timeLeft: number;
  lastBuyer: string;
  lastBuyerTeam: string;
  keysTotal: string;
  keyPrice: string;
  activeEvent?: string;
  isBloodMoon: boolean;
  isAssassinMode: boolean;
  isGoldenRound: boolean;
}

export interface UseGameEventsOptions {
  url?: string;
  autoConnect?: boolean;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  onEvent?: (event: GameEvent) => void;
  onStateUpdate?: (state: GameState) => void;
}

export interface UseGameEventsReturn {
  connected: boolean;
  connecting: boolean;
  events: GameEvent[];
  gameState: GameState | null;
  connect: () => void;
  disconnect: () => void;
  clearEvents: () => void;
}

const DEFAULT_WS_URL = import.meta.env.VITE_WS_URL || 'wss://nt3d-events.wurkit.workers.dev/ws';
const MAX_EVENTS_STORED = 100;

export function useGameEvents(options: UseGameEventsOptions = {}): UseGameEventsReturn {
  const {
    url = DEFAULT_WS_URL,
    autoConnect = true,
    reconnectInterval = 3000,
    maxReconnectAttempts = 10,
    onEvent,
    onStateUpdate,
  } = options;

  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [events, setEvents] = useState<GameEvent[]>([]);
  const [gameState, setGameState] = useState<GameState | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const addEvent = useCallback((event: GameEvent) => {
    setEvents((prev) => {
      const newEvents = [event, ...prev];
      // Keep only the most recent events
      return newEvents.slice(0, MAX_EVENTS_STORED);
    });
    onEvent?.(event);
  }, [onEvent]);

  const handleMessage = useCallback((message: MessageEvent) => {
    try {
      const data = JSON.parse(message.data);

      if (data.type === 'state') {
        setGameState(data.state);
        onStateUpdate?.(data.state);
      } else if (data.type === 'event') {
        addEvent(data.event);
      } else if (data.type === 'ping') {
        // Respond to keepalive
        wsRef.current?.send(JSON.stringify({ type: 'pong' }));
      }
    } catch (error) {
      console.error('[WS] Failed to parse message:', error);
    }
  }, [addEvent, onStateUpdate]);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    if (wsRef.current?.readyState === WebSocket.CONNECTING) return;

    setConnecting(true);

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[WS] Connected');
        setConnected(true);
        setConnecting(false);
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = handleMessage;

      ws.onerror = (error) => {
        console.error('[WS] Error:', error);
      };

      ws.onclose = (event) => {
        console.log('[WS] Disconnected:', event.code, event.reason);
        setConnected(false);
        setConnecting(false);
        wsRef.current = null;

        // Auto-reconnect if not a clean close
        if (event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current++;
          const delay = reconnectInterval * Math.pow(1.5, reconnectAttemptsRef.current - 1);
          console.log(`[WS] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current})`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        }
      };
    } catch (error) {
      console.error('[WS] Connection failed:', error);
      setConnecting(false);
    }
  }, [url, reconnectInterval, maxReconnectAttempts, handleMessage]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    reconnectAttemptsRef.current = maxReconnectAttempts; // Prevent auto-reconnect
    
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnected');
      wsRef.current = null;
    }
    setConnected(false);
  }, [maxReconnectAttempts]);

  const clearEvents = useCallback(() => {
    setEvents([]);
  }, []);

  // Auto-connect on mount
  useEffect(() => {
    if (autoConnect) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [autoConnect, connect, disconnect]);

  // Visibility change handling - reconnect when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !connected && autoConnect) {
        reconnectAttemptsRef.current = 0;
        connect();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [connected, autoConnect, connect]);

  return {
    connected,
    connecting,
    events,
    gameState,
    connect,
    disconnect,
    clearEvents,
  };
}

/**
 * Hook for real-time countdown timer
 */
export function useCountdown(endTime: number | null): {
  timeLeft: number;
  formatted: string;
  isExpired: boolean;
} {
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    if (!endTime) {
      setTimeLeft(0);
      return;
    }

    const updateTimer = () => {
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((endTime - now) / 1000));
      setTimeLeft(remaining);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [endTime]);

  const hours = Math.floor(timeLeft / 3600);
  const minutes = Math.floor((timeLeft % 3600) / 60);
  const seconds = timeLeft % 60;

  const formatted = hours > 0
    ? `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    : `${minutes}:${seconds.toString().padStart(2, '0')}`;

  return {
    timeLeft,
    formatted,
    isExpired: timeLeft === 0,
  };
}

/**
 * Hook for event-based notifications
 */
export function useEventNotifications(events: GameEvent[]): void {
  const lastEventRef = useRef<string | null>(null);

  useEffect(() => {
    if (events.length === 0) return;
    
    const latestEvent = events[0];
    if (lastEventRef.current === latestEvent.txHash) return;
    lastEventRef.current = latestEvent.txHash || null;

    // Play sound or vibrate based on event type
    const notificationEvents = ['bloodMoon', 'assassin', 'reaper', 'extinction', 'roundEnd'];
    
    if (notificationEvents.includes(latestEvent.type)) {
      // Vibrate on mobile
      if (navigator.vibrate) {
        navigator.vibrate([100, 50, 100]);
      }
      
      // Could also play audio here
      // playEventSound(latestEvent.type);
    }
  }, [events]);
}

export default useGameEvents;
